import { useMemo, useState } from 'react';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import {
  Card, Chip, DataTable, EmptyState, Modal, Pane, PaneHead, StatCard, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { MonthFilterRow } from '../components/MonthFilterRow';
import {
  dayNoYear, doctorName, monthLabel, monthRange, num,
  shortName, stamp,
  monthChip,
} from '../lib/scope';
import { useReportMonth } from '../lib/useReportMonth';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { useAppointments, useAuditLog, useInvoices, useProviders } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

type Family = 'all' | 'discounts' | 'refunds' | 'edits' | 'voids';

/** What counts as a money object in the audit log's `resource` column. */
const MONEY_RESOURCE = /invoice|bill|payment|claim/i;

/**
 * Clinic manager · Discounts, refunds & edits — `g_dre.dc.html`.
 *
 * TRANSPARENCY OVER PREVENTION, which is the board's closing line and the
 * reason this screen exists at all: nothing here was blocked, all of it is
 * readable, and every reason was typed at the moment it happened.
 *
 * TWO SOURCES, DELIBERATELY. A discount, a refund and a void are states of a
 * BILL — they are read from the invoices themselves, because that is where the
 * money is. An EDIT is an act, and acts live in the audit log. Reading edits
 * off invoices would count a bill that was corrected twice as one, and reading
 * discounts off the log would miss every discount given before logging was on.
 *
 * WHAT IS THIN: the invoice carries `notes` (encrypted) but no reason field for
 * the discount itself, and the audit log's `details` is free-form. Where a
 * typed reason exists it is printed; where it does not, the row says so rather
 * than inventing a motive.
 */
export function ManagerEditsPage() {
  const { surface } = useShellIdentity();
  const narrow = useNarrowViewport();
  const onManagerSurface = surface === 'clinic-manager';
  const go = useNavigate();
  const { month, setMonth, compare, setCompare } = useReportMonth();
  const [family, setFamily] = useState<Family>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /* "By person ▾" carries an arrow, so it opens a real picker (BRIEF §4.8). */
  const [personOpen, setPersonOpen] = useState(false);
  const [person, setPerson] = useState<string | null>(null);

  const range = useMemo(() => monthRange(month), [month]);
  const invoices = useInvoices(range);
  const appointments = useAppointments(range);
  const { providers } = useProviders();
  const log = useAuditLog({
    category: 'data_modification',
    startDate: `${range.start}T00:00:00.000Z`,
    endDate: `${range.end}T23:59:59.999Z`,
    limit: 100,
  });

  usePageMeta({ title: 'Discounts, refunds & edits' });

  /* "28 Aug 6:58 pm" on the board — the MOMENT the act happened, which for a
     bill raised on the day it is for is its `createdAt`. A back-dated bill
     (a correction typed a week later) would print a time that belongs to a
     different day, so those fall back to the day the bill is FOR. */
  const whenOf = (inv: { date: string; createdAt?: string }) =>
    inv.createdAt && inv.createdAt.slice(0, 10) === inv.date
      ? stamp(inv.createdAt)
      /* "26 Aug", not "26 Aug 2026" — the month is already in the filter row
         above, and the year turns a 14% column into two lines. `monthDay` is
         the CHART AXIS formatter and prints "Aug 26"; every date on this
         surface reads day-then-month, which is what the board draws. */
      : dayNoYear(inv.date);

  const providerOfAppointment = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of appointments.page.rows) if (a.providerId) m.set(a.id, a.providerId);
    return m;
  }, [appointments.page.rows]);
  const providerById = useMemo(() => {
    const m = new Map<string, (typeof providers)[number]>();
    for (const p of providers) m.set(p.id, p);
    return m;
  }, [providers]);

  interface Event {
    id: string;
    /** The bill behind it, so "Open bill ›" can open it. */
    invoiceId?: string;
    kind: Family;
    when: string;
    sortKey: string;
    what: string;
    badge?: { text: string; kind: 'warn' | 'danger' };
    who: string;
    reason: string;
    before?: number;
    after?: number;
    doctor?: string;
    settled?: string;
  }

  const events: Event[] = useMemo(() => {
    const out: Event[] = [];

    for (const inv of invoices.page.rows) {
      const discount = Number(inv.discountAmount || 0);
      const gross = Number(inv.subtotal || 0) + Number(inv.taxAmount || 0);
      const doctorId = inv.appointmentId ? providerOfAppointment.get(inv.appointmentId) : undefined;
      const doctor = doctorId ? doctorName(providerById.get(doctorId)) : 'no doctor on the bill';
      const who = inv.issuedBy ? shortName(inv.issuedBy) : 'Not recorded';

      if (discount > 0) {
        const pct = gross > 0 ? Math.round((discount / gross) * 100) : 0;
        out.push({
          id: `d-${inv.id}`, invoiceId: inv.id, kind: 'discounts', when: whenOf(inv), sortKey: inv.date,
          what: `Discount −${num(discount)} · #${inv.invoiceNumber}`,
          /* Warn, whatever the percentage — the board colours a discount amber
             and a refund red, because a discount is a conversation and money
             leaving the till is not. The threshold is a story the pane tells,
             not a second colour. */
          badge: { text: `${pct}%`, kind: 'warn' },
          who, reason: 'no reason field on a bill',
          before: gross, after: Number(inv.total || 0), doctor,
          settled: inv.paymentMethod ? `paid ${inv.paymentMethod.replace('_', ' ')} ${num(inv.amountPaid || 0)}` : 'unsettled',
        });
      }
      if (inv.status === 'cancelled') {
        out.push({
          id: `v-${inv.id}`, invoiceId: inv.id, kind: 'voids', when: whenOf(inv), sortKey: inv.date,
          what: `Void · #${inv.invoiceNumber}`,
          who, reason: 'no reason field on a bill', doctor,
          before: Number(inv.total || 0), after: 0,
        });
      }
      /* A REFUND is money that went back: paid more than the bill now asks. */
      const over = Number(inv.amountPaid || 0) - Number(inv.total || 0);
      if (over > 0) {
        out.push({
          id: `r-${inv.id}`, invoiceId: inv.id, kind: 'refunds', when: whenOf(inv), sortKey: inv.date,
          what: `Refund −${num(over)} · #${inv.invoiceNumber}`,
          badge: { text: inv.paymentMethod?.replace('_', ' ') ?? 'method not set', kind: 'danger' },
          who, reason: 'no reason field on a bill', doctor,
          before: Number(inv.amountPaid || 0), after: Number(inv.total || 0),
        });
      }
    }

    /* "BILL EDITS AFTER SIGN" IS ABOUT BILLS. `data_modification` is every
       modification the clinic makes — a chart edit, a schedule change, a
       template rename — so counting the whole category under that label would
       put a true number beside a false claim. Narrowed to the money objects. */
    for (const entry of log.page.rows) {
      if (!MONEY_RESOURCE.test(entry.resource || '')) continue;
      out.push({
        id: `e-${entry.id}`, kind: 'edits', when: stamp(entry.createdAt),
        sortKey: entry.createdAt.slice(0, 10),
        what: `${entry.action} · ${entry.resource}`,
        who: shortName(entry.user),
        reason: typeof entry.details?.reason === 'string' ? `“${entry.details.reason}”` : 'no reason typed',
      });
    }

    out.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1));
    return out;
  }, [invoices.page.rows, log.page.rows, providerOfAppointment, providerById]);

  const counts = {
    discounts: events.filter((e) => e.kind === 'discounts'),
    refunds: events.filter((e) => e.kind === 'refunds'),
    edits: events.filter((e) => e.kind === 'edits'),
    voids: events.filter((e) => e.kind === 'voids'),
  };
  const discountTotal = invoices.page.rows.reduce((s, i) => s + Number(i.discountAmount || 0), 0);
  const refundTotal = counts.refunds.reduce(
    (s, e) => s + ((e.before ?? 0) - (e.after ?? 0)), 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events
      .filter((e) => family === 'all' || e.kind === family)
      .filter((e) => !person || e.who === person)
      .filter((e) => !q || `${e.what} ${e.who} ${e.reason}`.toLowerCase().includes(q));
  }, [events, family, query, person]);

  const selected = selectedId ? filtered.find((e) => e.id === selectedId) ?? null : null;

  /* By person — aggregate, never a patient name. */
  const byPerson = useMemo(() => {
    const m = new Map<string, { discounts: number; refunds: number; edits: number; voids: number }>();
    for (const e of events) {
      const row = m.get(e.who) ?? { discounts: 0, refunds: 0, edits: 0, voids: 0 };
      if (e.kind !== 'all') row[e.kind] += 1;
      m.set(e.who, row);
    }
    return [...m.entries()].sort((a, b) =>
      (b[1].discounts + b[1].edits) - (a[1].discounts + a[1].edits));
  }, [events]);

  const rows: TableRow[] = filtered.slice(0, 40).map((e) => ({
    id: e.id,
    cells: {
      when: <span className="t-sec mut">{e.when}</span>,
      what: (
        <div>
          <p className="t-strong">
            {e.what}{' '}
            {e.badge ? <Chip kind={e.badge.kind} small>{e.badge.text}</Chip> : null}
          </p>
        </div>
      ),
      who: <span className="t-sec mut">{e.who}</span>,
      reason: <span className="t-sec mut">{e.reason}</span>,
    },
  }));

  const paneSig = `${selected?.id ?? ''}`;

  const crumb = usePhoneCrumb('Reports', `Discounts & edits · ${monthChip(month).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Discounts, refunds & edits',
      sub: `${monthLabel(month)} · the audit trail as a report`,
      search: 'Search a bill, a person, a reason…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`money-edits-${month}`, ['When', 'What', 'Who', 'Reason'],
            filtered.map((e) => [e.when, e.what, e.who, e.reason])),
      },
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The selected event"
              title={selected.what.split(' · ').reverse().join(' — ')}
              sub="the audit trail, one line"
            />
          }
        >
          {selected.before !== undefined && selected.after !== undefined ? (
            <Card>
              <p className="t-ovl mut">Before → after</p>
              <p className="t-sec sec" style={{ marginTop: 4 }}>
                <s className="mut">{num(selected.before)}</s> → <span className="w6">{num(selected.after)}</span>
                {' '}· −{num(selected.before - selected.after)}
                {selected.before > 0 ? ` · ${Math.round(((selected.before - selected.after) / selected.before) * 100)}%` : ''}
              </p>
              {/* THE BOARD'S NEXT LINE IS "above your 10% threshold → the alert
                  fired", and it is a SAMPLE NARRATIVE. There is no configurable
                  discount threshold anywhere in this product and no alert was
                  raised for this bill — asserting either inside a money-audit
                  pane is exactly the fabrication §4.9 forbids, and this is the
                  one screen whose whole subject is what really happened. What
                  is true is printed instead. */}
              <p className="t-cap mut">
                no discount threshold is configurable in this product yet, so nothing was alerted
                on — this is the bill as it stands
              </p>
            </Card>
          ) : null}
          <Card>
            <p className="t-ovl mut">Given by · when</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>{selected.who} · {selected.when}</p>
            <p className="t-cap mut">reason: {selected.reason}</p>
          </Card>
          {selected.doctor ? (
            <Card>
              <p className="t-ovl mut">The bill</p>
              <p className="t-sec sec" style={{ marginTop: 4 }}>
                {selected.doctor}{selected.settled ? ` · ${selected.settled}` : ''}
              </p>
            </Card>
          ) : null}
          <Card kind="accent">
            <p className="t-sec brand">
              A bill carries no reason field, so “manager’s friend” has nowhere to be typed yet.
              The act is logged; the motive is not.
            </p>
          </Card>
          <p style={{ marginTop: 12 }}>
            {selected.invoiceId ? (
              <>
                <button type="button" className="dchip"
                  style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
                  onClick={() => go(`/billing/${selected.invoiceId}`)}>
                  Open bill ›
                </button>{' '}
              </>
            ) : null}
            {/* THE AUDIT LOG IS THE MANAGER'S SUBTREE, gated on
                hospital_settings:read. This page is also mounted on the
                accountant's surface, where that link would be refused — and a
                link that 403s is worse than no link, so it is drawn for
                whoever can actually follow it. */}
            {onManagerSurface ? (
              <button type="button" className="dchip"
                style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
                onClick={() => go('/clinic-manager/audit')}>
                Full edit history ›
              </button>
            ) : null}
          </p>
        </Pane>
      ) : undefined,
    },
    [month, compare, query, family, paneSig, filtered.length, person, selectedId],
  );

  const chip = (key: Family, label: string) => (
    <Chip key={key} kind={family === key ? 'brand' : 'outline'} small
      className={family === key ? 'w6' : undefined}
      onClick={family === key ? undefined : () => { setFamily(key); setSelectedId(null); }}>
      {label}
    </Chip>
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <Modal
        open={personOpen}
        onClose={() => setPersonOpen(false)}
        title="By person"
        sub="narrow the month to one person's money edits"
        width={400}
        foot={<button type="button" className="btn btn-outline" onClick={() => setPersonOpen(false)}>Close</button>}
      >
        <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0' }}>
          <button type="button" className="mi"
            onClick={() => { setPerson(null); setPersonOpen(false); setSelectedId(null); }}>
            <span>Everyone</span>
            {person ? null : <span className="t-cap brand w6">showing</span>}
          </button>
          {byPerson.map(([who]) => (
            <button key={who} type="button" className="mi"
              onClick={() => { setPerson(who); setPersonOpen(false); setSelectedId(null); }}>
              <span>{who}</span>
              {person === who ? <span className="t-cap brand w6">showing</span> : null}
            </button>
          ))}
        </div>
      </Modal>

      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={(m) => { setMonth(m); setSelectedId(null); }}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
          wrapOnPhone
          extra={
            <>
              {chip('all', 'All')}
              {chip('discounts', 'Discounts')}
              {chip('refunds', 'Refunds')}
              {chip('edits', 'Edits')}
              {chip('voids', 'Voids')}
              {narrow ? (
                <Chip kind="outline" small onClick={() => setPersonOpen(true)}>
                  {person ? `${person} ▾` : 'By person ▾'}
                </Chip>
              ) : <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>}
            </>
          }
        />

        <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          <StatCard label="Discounts" value={`${counts.discounts.length} · ${num(discountTotal)}`} tone="warn" />
          <StatCard label="Refunds" value={`${counts.refunds.length} · ${num(refundTotal)}`} tone="danger" />
          <StatCard label="Bill edits after sign" value={num(counts.edits.length)} />
          <StatCard label="Voids" value={num(counts.voids.length)} />
        </div>

        <DataTable
          caption="Money events"
          columns={[
            { key: 'when', label: 'When', width: 14 },
            { key: 'what', label: 'What', width: 46, role: 'body' },
            /* The person is the chip, not the reason: a typed reason is a
               sentence, and a sentence in the chip slot crowds out the edit
               it is about. It stacks under the body instead. */
            { key: 'who', label: 'Who', width: 14, role: 'trailing' },
            { key: 'reason', label: 'Reason typed', width: 26 },
          ]}
          rows={rows}
          total={filtered.length}
          selectedId={selectedId}
          onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
          empty={
            <EmptyState
              title="Nothing was discounted, refunded, edited or voided"
              sub="A bill given a discount, refunded, corrected or cancelled lands here the moment it happens, with whoever did it beside it."
            />
          }
        />

        <div style={{ height: 12 }} />

        <Card>
          <p className="t-ovl mut mb8">By person</p>
          {byPerson.length ? (
            <p className="t-sec sec">
              {byPerson.slice(0, 4).map(([who, c], i) => (
                <span key={who}>
                  {i > 0 ? <>&nbsp;·&nbsp;</> : null}
                  {who} — {c.discounts} discount{c.discounts === 1 ? '' : 's'} · {c.refunds} refund
                  {c.refunds === 1 ? '' : 's'} · {c.edits} edit{c.edits === 1 ? '' : 's'}
                </span>
              ))}
            </p>
          ) : (
            <p className="t-sec mut">Nobody gave a discount, a refund or an edit this month.</p>
          )}
          <p className="t-cap mut mt4">
            a pattern is a conversation, not an alert — aggregate, never a patient name
          </p>
          <p className="mt8">
            <button type="button" className="dchip"
              style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
              onClick={() => setPersonOpen(true)}>
              {person ? `${person} ▾` : 'By person ▾'}
            </button>
          </p>
        </Card>

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Transparency over prevention: nothing here was blocked, all of it is readable. A bill has
          no reason field yet, so the “reason typed” column says so instead of guessing.
        </p>
      </div>
    </div>
  );
}
